import { HttpErrorResponse, HttpInterceptorFn, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { getTenantStorage } from '../utils/tenant-context';

function addAuthHeader(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function handleRefresh(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService
) {
  if (authService.isRefreshing) {
    return authService.waitForRefresh().pipe(
      switchMap(token => next(addAuthHeader(req, token)))
    );
  }

  authService.isRefreshing = true;
  authService.refreshToken$.next(null);

  return authService.refreshAccessToken().pipe(
    switchMap(newToken => {
      authService.isRefreshing = false;
      authService.refreshToken$.next(newToken);
      return next(addAuthHeader(req, newToken));
    }),
    catchError(refreshError => {
      authService.isRefreshing = false;
      authService.redirectToLogin(
        'token_refresh_failed',
        'Session expired. Please login again.',
        { error: authService.formatHttpError(refreshError) }
      );
      return throwError(() => refreshError);
    })
  );
}

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const isAuthEndpoint = req.url.includes('/v1/auth/');
  const authToken = getTenantStorage('access_token');

  const outgoing = authToken ? addAuthHeader(req, authToken) : req;

  return next(outgoing).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !isAuthEndpoint) {
        return handleRefresh(req, next, authService);
      }
      return throwError(() => error);
    })
  );
};
